import React, { useEffect, useState, useMemo } from 'react'
import CommonTablePage, { CommonTablePagePropsInterface } from '../../../components/common/common-table/common-table-page'
import moment from 'moment-timezone'
import { DatePicker, TimePicker, Input, Select, Button, Space, Form, message } from 'antd'
import * as XLSX from 'xlsx'

const { RangePicker } = DatePicker

const getThisDay = () => {
  const today = moment().startOf('day')
  return {
    startStr: today.format('YYYY-MM-DD'),
    endStr: today.format('YYYY-MM-DD'),
    startMoment: today,
    endMoment: today
  }
}

const basePostUrl = 'attendance/all-records'

function buildUtcIsoFromLocalDateAndTime(baseDate: any, hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number)
  const dateOnly = moment(baseDate).format('YYYY-MM-DD')
  const local = moment(`${dateOnly} ${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`, 'YYYY-MM-DD HH:mm:ss')
  return local.utc().toISOString()
}

const AttendanceRecordsPage: React.FC = () => {
  const [triggerResetData, setTriggerResetData] = useState(false)
  const [url, setUrl] = useState<string>(basePostUrl)

  const [employeeName, setEmployeeName] = useState<string>()
  const [attendanceGroup, setAttendanceGroup] = useState<string>()
  const [dateRange, setDateRange] = useState<[any, any] | null>([
    getThisDay().startMoment,
    getThisDay().endMoment,
  ])
  const [checkTime, setCheckTime] = useState<any>(null)
  const [exportRows, setExportRows] = useState<any[]>([])

  const [restRequestOptions, setRestRequestOptions] = useState<any>(() => {
    const { startStr, endStr } = getThisDay()
    return {
      method: 'post',
      data: {
        EmployeeName: null,
        attendanceGroup: null,
        DateStartUtc: moment(startStr, 'YYYY-MM-DD').utc().toISOString(),
        DateEndUtc: moment(endStr, 'YYYY-MM-DD').utc().toISOString(),
        CheckIntUtc: null,
      },
    }
  })

  // 初始加载
  useEffect(() => {
    setUrl(basePostUrl)
    setTriggerResetData(t => !t)
  }, [])

  // 点击 Apply 时触发
  const applyWithParams = () => {
    const startIso = (dateRange?.[0]
      ? moment(dateRange[0]).utc()
      : moment(getThisDay().startStr, 'YYYY-MM-DD').utc()
    ).toISOString()

    const endIso = (dateRange?.[1]
      ? moment(dateRange[1]).utc()
      : moment(getThisDay().endStr, 'YYYY-MM-DD').utc()
    ).toISOString()

    const nextBody = {
      EmployeeName: employeeName?.trim() || null,
      attendanceGroup: attendanceGroup || null,
      DateStartUtc: startIso,
      DateEndUtc: endIso,
      CheckIntUtc: checkTime
        ? buildUtcIsoFromLocalDateAndTime(dateRange?.[0] || new Date(), moment(checkTime).format('HH:mm'))
        : null,
    }

    setRestRequestOptions({ method: 'post', data: nextBody })
    setUrl(basePostUrl)
    setTriggerResetData(t => !t)   // 触发 CommonTablePage 重新请求
  }

  const mappingRenderData = (rows: any[]) => {
    const mapped = (rows || []).map((r) => {
      const checkMoment = r.checkTime ? moment.utc(r.checkTime) : null
      const checkLocal = checkMoment ? checkMoment.tz('Pacific/Auckland') : null

      return {
        ...r,
        employeeName: r.employeeName ?? '',
        attendanceGroup: r.attendanceGroup ?? '',
        dateOnly: r.date ?? (checkLocal ? checkLocal.format('YYYY-MM-DD') : ''),
        attendanceType: r.checkType ?? '',
        checkTimeUtc: checkLocal ? checkLocal.format('HH:mm') : '',
      }
    })
    // 排序：先按 attendanceGroup，再按 employeeName
    mapped.sort((a, b) => {
      const g = a.attendanceGroup.localeCompare(b.attendanceGroup)
      if (g !== 0) return g
      return a.employeeName.localeCompare(b.employeeName)
    })
    setExportRows(mapped)
    return mapped
  }

  const columns = [
    { title: 'Name', field: 'employeeName' },
    { title: 'Attendance Group', field: 'attendanceGroup' },
    { title: 'Date', field: 'dateOnly' },
    { title: 'Attendance Type', field: 'attendanceType' },
    { title: 'Time', field: 'checkTimeUtc' },
  ]

  const tableProps: Partial<CommonTablePagePropsInterface> = {
    title: 'Attendance Records',
    column: columns as any,
    isNotAddable: true,
    isNotEditable: true,
    isNotDeletable: true,
    getAllUrl: url,
    restRequestOptions,
    mappingRenderData,
    triggerResetData,
  }

  const handleExportXlsx = () => {
    if (!exportRows || exportRows.length === 0) {
      message.info('There are currently no exportable data.')
      return
    }

    // 顶部两行：筛选条件
    const startStr = dateRange?.[0] ? moment(dateRange[0]).format('YYYY-MM-DD') : getThisDay().startStr
    const endStr = dateRange?.[1] ? moment(dateRange[1]).format('YYYY-MM-DD') : getThisDay().endStr

    const headerLines = [
      [`DateStart(NZ): ${moment(startStr, 'YYYY-MM-DD').local().format('YYYY-MM-DD HH:mm')}`,
      `DateEnd(NZ): ${moment(endStr, 'YYYY-MM-DD').local().format('YYYY-MM-DD HH:mm')}`,
      `Employee: ${employeeName || '-'}`, `AttendanceGroup: ${attendanceGroup || '-'}`],
      [
        `Exported(NZ): ${new Date().toLocaleString('en-NZ', { timeZone: 'Pacific/Auckland' })}`],
      [] // 空行
    ]

    // 列头
    const heads = ['Name', 'Attendance Group', 'Date', 'Attendance Type', 'Time']

    // 数据（使用表格显示过的字段，已是本地时间字符串）
    const body = exportRows.map(r => [
      r.employeeName, r.attendanceGroup, r.dateOnly, r.attendanceType, r.checkTimeUtc
    ])

    const allRows = [...headerLines, heads, ...body]
    const ws = XLSX.utils.aoa_to_sheet(allRows)

    // 自动筛选
    ws['!autofilter'] = {
      ref: XLSX.utils.encode_range({
        s: { r: headerLines.length, c: 0 },
        e: { r: headerLines.length + Math.max(body.length, 1), c: heads.length - 1 }
      })
    }
    // 冻结到表头
    ws['!freeze'] = { xSplit: 0, ySplit: headerLines.length + 1 }

    // 自适应列宽（简单实现）
    const colCount = heads.length
    const widths = new Array(colCount).fill(10)
    allRows.forEach(row => {
      row.forEach((cell, i) => {
        const s = cell == null ? '' : String(cell)
        widths[i] = Math.max(widths[i], s.length + 2)
      })
    })
    ws['!cols'] = widths.map(w => ({ wch: Math.min(Math.max(w, 8), 40) }))

    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'AttendanceRecords')

    const fname = `AttendanceRecords${startStr}_to_${endStr}.xlsx`
    XLSX.writeFile(wb, fname)
  }

  return (
    <div style={{ padding: 16 }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Form layout="inline" onFinish={() => applyWithParams()} >
          <Form.Item label="Date Range">
            <RangePicker
              value={dateRange as any}
              onChange={(vals) => setDateRange(vals as any)}
              format="YYYY/MM/DD"
            />
          </Form.Item>

          <Form.Item label="Name">
            <Input
              value={employeeName}
              onChange={(e) => setEmployeeName(e.target.value)}
              style={{ width: 200 }}
              allowClear
            />
          </Form.Item>

          <Form.Item label="Attendance Group">
            <Select
              value={attendanceGroup}
              onChange={setAttendanceGroup}
              allowClear
              style={{ width: 160 }}
              options={[
                { label: 'Office', value: 'Office' },
                { label: 'Production', value: 'Production' },
                { label: 'Warehouse', value: 'Warehouse' },
                { label: 'Printing', value: 'Printing' },
                { label: 'AttendanceFree', value: 'AttendanceFree' },
              ]}
            />
          </Form.Item>

          <Form.Item label="Check Time">
            <TimePicker
              value={checkTime}
              onChange={setCheckTime}
              format="HH:mm"
              minuteStep={5}
              allowClear
            />
          </Form.Item>

          <Form.Item>
            <Space style={{ margin: 10 }}>
              <Button type="primary" htmlType="submit">Apply</Button>
              <Button onClick={handleExportXlsx}>Download XLSX</Button>
            </Space>
          </Form.Item>
        </Form>

        <CommonTablePage {...tableProps} />
      </Space>
    </div>
  )
}
export default AttendanceRecordsPage